import { NA, signedUsd, usd } from '../../../utilities/format';
import { t } from '../../../i18n/i18n';

/**
 * Affiliate dashboard — what `/affiliate/dashboard/cards` and `/trend` send,
 * turned into what the page draws. No sample figures: before the first
 * response a card reads N/A, not a made-up number.
 */

/** The trend tabs → the API's `metric`. */
export const METRIC_PARAM_MAP = {
  Revenue: 'revenue',
  Click: 'clicks',
  Sales: 'sales',
  'Sign-ups': 'signups',
};

/** The range tabs are the API's own `range` values, upper-cased for the tab. */
export const TREND_RANGES = ['1D', '1W', '1M', '3M', 'YTD', 'ALL'];

/** Which cards are money, so they read "$400.00" rather than "400". */
const CURRENCY_CARDS = new Set(['revenue', 'new_signup_revenue', 'recurring_revenue', 'commission']);

/** The six Figma cards (1:16777), in order. The API's `commission` isn't one of them. */
export const CARD_DEFINITIONS = [
  { key: 'revenue', label: 'Revenue', icon: 'affiliate-revenue' },
  { key: 'clicks', label: 'Clicks', icon: 'affiliate-clicks' },
  { key: 'sales', label: 'Sales', icon: 'affiliate-sales' },
  { key: 'signups', label: 'Sign-ups', icon: 'affiliate-signups' },
  { key: 'new_signup_revenue', label: 'New signup revenue', icon: 'affiliate-new-signup-revenue' },
  { key: 'recurring_revenue', label: 'Recurring revenue', icon: 'affiliate-revenue' },
];

const INTEGER = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });

export const isCurrencyMetric = (key) => CURRENCY_CARDS.has(key);

/** A card's (or a series') figure as the page writes it. */
export const formatMetricValue = (key, value) => {
  if (value == null || !Number.isFinite(Number(value))) return NA;
  return isCurrencyMetric(key) ? usd(value, { decimals: 2 }) : INTEGER.format(Number(value));
};

const toneOf = (n) => (n > 0 ? 'up' : n < 0 ? 'down' : null);

/** "+20.0%" — the API rounds to one place already; this only signs it. */
const signedPct = (pct) => `${pct > 0 ? '+' : ''}${pct}%`;

/**
 * The line under a card's figure. The two revenue splits say what share of
 * the period's revenue they are; every other card says how it moved against
 * the previous period of the same length.
 */
const cardNote = (key, card) => {
  if (card.share_of_revenue != null) {
    return { note: t('{{pct}}% of total revenue', { pct: card.share_of_revenue }), noteTone: null };
  }
  if (card.pct_change == null) return { note: '', noteTone: null };
  return {
    note: t('{{change}} vs previous period', { change: signedPct(card.pct_change) }),
    noteTone: toneOf(card.pct_change),
  };
};

/** `cards` from `/affiliate/dashboard/cards` → the six tiles. */
export const buildMetricsCards = (cards) =>
  CARD_DEFINITIONS.map((def) => {
    const card = cards?.[def.key];
    if (!card) return { ...def, value: NA, note: '', noteTone: null };
    return {
      ...def,
      value: formatMetricValue(def.key, card.value),
      ...cardNote(def.key, card),
    };
  });

/**
 * The figure beside the trend's title: the series total, and how that
 * metric moved against the previous period of the same range — "+$1,940.20
 * +18.4%" — read off the cards for that range.
 */
export const trendSummary = (metric, series, card) => {
  const value = formatMetricValue(metric, series?.total);
  if (!card || card.pct_change == null || card.previous == null) {
    return { value, delta: '', deltaTone: null };
  }
  const change = Number(card.value) - Number(card.previous);
  const amount = isCurrencyMetric(metric)
    ? signedUsd(change, { decimals: 2 })
    : `${change > 0 ? '+' : change < 0 ? '-' : ''}${INTEGER.format(Math.abs(change))}`;
  return { value, delta: `${amount} ${signedPct(card.pct_change)}`, deltaTone: toneOf(change) };
};

/** A series' `points` as the line chart's `{x, y}`. Hourly `t` parses as a date-time. */
export const toChartPoints = (series) =>
  (series?.points ?? []).map((p) => ({ x: p.t, y: Number(p.value) || 0 }));
